import React from 'react';

import { FILLS, FONTS, RADII, SEARCH_LAYER, STROKES } from '../../styles/tokens';
import type { SurfaceTone } from '../../styles/tokens';

interface StepperProps {
  label: string;
  helper?: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  // `micro` sets the label as a form micro-label, for forms whose other
  // fields carry micro-labels; the default keeps the 14px sentence-case label.
  labelStyle?: 'body' | 'micro';
  surface?: SurfaceTone;
}

const LABEL_STYLES: Record<'body' | 'micro', React.CSSProperties> = {
  body: { fontFamily: FONTS.body, fontSize: '14px', color: FILLS.textPrimary },
  micro: {
    fontFamily: FONTS.body,
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: FILLS.textMuted,
  },
};

/**
 * Compact + / − stepper used for guest mix (Adults/Children on the NextPax
 * booking card) and Extras (Pets/Vehicles/Extra beds in
 * `NextPaxDetailSections`). Shared so the card and the editorial sections
 * stay visually + ARIA-consistent.
 *
 * Defaults: `min = 0`, `max = 99`. Buttons disable + dim at boundaries;
 * each carries an `aria-label` of the form "Increase <label>" /
 * "Decrease <label>" so tests and screen readers can target them
 * unambiguously.
 */
const Stepper: React.FC<StepperProps> = ({
  label,
  helper,
  value,
  min = 0,
  max = 99,
  onChange,
  labelStyle = 'body',
  surface = 'dark',
}) => {
  const isCream = surface === 'cream';
  const labelBaseStyle = LABEL_STYLES[labelStyle];
  const labelColor = isCream ? SEARCH_LAYER.ink : labelBaseStyle.color;
  const clamp = (next: number) => Math.max(min, Math.min(max, next));
  // The hit box grows to the 44px target while the visible circle
  // stays 32px — the button itself is the transparent 44px box, and a
  // `data-visual-circle` inner span carries the actual border/background so
  // the control doesn't visually grow.
  const hitBoxStyle: React.CSSProperties = {
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  };
  const visualCircleStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: RADII.pill,
    background: isCream ? SEARCH_LAYER.card : FILLS.surface,
    border: `1px solid ${isCream ? SEARCH_LAYER.stroke : STROKES.subtle}`,
    color: isCream ? SEARCH_LAYER.ink : FILLS.textPrimary,
    fontFamily: FONTS.body,
    fontSize: '16px',
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '10px 0',
      }}
    >
      <div>
        <div style={{ ...labelBaseStyle, color: labelColor }}>{label}</div>
        {helper && (
          <div
            style={{
              fontFamily: FONTS.body,
              fontSize: '12px',
              color: isCream ? SEARCH_LAYER.inkMuted : FILLS.textMuted,
            }}
          >
            {helper}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= min}
          style={hitBoxStyle}
        >
          <span
            data-visual-circle
            style={{ ...visualCircleStyle, opacity: value <= min ? 0.4 : 1 }}
          >
            −
          </span>
        </button>
        <span
          style={{
            minWidth: '20px',
            textAlign: 'center',
            fontFamily: FONTS.body,
            fontSize: '15px',
            color: isCream ? SEARCH_LAYER.ink : FILLS.textPrimary,
          }}
          aria-label={`${label} ${value}`}
        >
          {value}
        </span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= max}
          style={hitBoxStyle}
        >
          <span
            data-visual-circle
            style={{ ...visualCircleStyle, opacity: value >= max ? 0.4 : 1 }}
          >
            +
          </span>
        </button>
      </div>
    </div>
  );
};

export default Stepper;
